/**
 * Module-level source of truth for the current Cognito ID token.
 *
 * This is intentionally decoupled from React state. The token rotates roughly
 * once an hour (silent refresh), and ~74 effects across the app depend on
 * AuthContext.idToken — pushing every rotated token into React state re-fires
 * all of them at once, which users perceive as the whole page "refreshing".
 *
 * Instead, the rotated token lives here (and in localStorage), and request-time
 * helpers (the axios request interceptor, authedFetch) read from here. React
 * state `idToken` only changes on real auth events (login / logout / user
 * change), so the effect cascade no longer fires on a routine refresh.
 */

const STORAGE_KEY = "idToken";

let currentToken = readFromStorage();

function readFromStorage(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

/** Current ID token — in-memory first, falling back to localStorage. */
export function getStoredIdToken(): string {
  if (!currentToken) {
    currentToken = readFromStorage();
  }
  return currentToken;
}

/** Update the current ID token in memory and localStorage. Empty clears it. */
export function setStoredIdToken(token: string): void {
  currentToken = token || "";
  try {
    if (token) {
      localStorage.setItem(STORAGE_KEY, token);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* localStorage unavailable — in-memory value still holds */
  }
}

/** Clear the stored ID token (logout / unrecoverable auth error). */
export function clearStoredIdToken(): void {
  setStoredIdToken("");
}
